import React from "react";
import { Box } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import {
  PersonOutlined,
  NotificationsOutlined,
  PeopleOutlined,
  BusinessOutlined,
  PlaceOutlined,
  PaymentsOutlined,
  BuildOutlined,
  LocalShippingOutlined,
  PaletteOutlined,
} from "@mui/icons-material";
import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { SettingsNavSection } from "../hooks/useSettingsNav";
import { dt } from "../../../shared/themes/redesignTokens";

const iconMap: Record<string, React.ReactNode> = {
  PersonOutlined: <PersonOutlined sx={{ fontSize: "20px" }} />,
  NotificationsOutlined: <NotificationsOutlined sx={{ fontSize: "20px" }} />,
  PeopleOutlined: <PeopleOutlined sx={{ fontSize: "20px" }} />,
  BusinessOutlined: <BusinessOutlined sx={{ fontSize: "20px" }} />,
  PlaceOutlined: <PlaceOutlined sx={{ fontSize: "20px" }} />,
  PaymentsOutlined: <PaymentsOutlined sx={{ fontSize: "20px" }} />,
  BuildOutlined: <BuildOutlined sx={{ fontSize: "20px" }} />,
  LocalShippingOutlined: <LocalShippingOutlined sx={{ fontSize: "20px" }} />,
  PaletteOutlined: <PaletteOutlined sx={{ fontSize: "20px" }} />,
};

interface SettingsSidebarProps {
  sections: SettingsNavSection[];
}

const SettingsSidebar: React.FC<SettingsSidebarProps> = ({ sections }) => {
  const location = useLocation();
  const { translate } = useTranslation("settings");
  const { userInfo } = useAppSelector((state) => state.customerAccount);

  const isActive = (path: string) =>
    location.pathname === `/settings/${path}` ||
    location.pathname.startsWith(`/settings/${path}/`);

  const initials =
    userInfo
      ? `${(userInfo.firstName || "").charAt(0)}${(userInfo.lastName || "").charAt(0)}`.toUpperCase()
      : "?";

  return (
    <Box
      sx={{
        width: "240px",
        minWidth: "240px",
        flexShrink: 0,
        animation: "fadeLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
      }}
    >
      <Box
        sx={{
          backgroundColor: dt.cardBg,
          borderRadius: dt.radiusLg,
          border: `1px solid ${dt.cardBorder}`,
          boxShadow: dt.cardShadow,
          padding: "20px 16px",
        }}
      >
        {/* User info section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: 2, px: "6px" }}>
          <Box
            sx={{
              width: "48px",
              height: "48px",
              minWidth: "48px",
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${dt.blue}, ${dt.blueHover})`,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: "16px",
              fontFamily: dt.font,
            }}
          >
            {initials}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: dt.textPrimary,
                fontFamily: dt.font,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {userInfo ? `${userInfo.firstName} ${userInfo.lastName}` : ""}
            </Box>
            <Box
              sx={{
                fontSize: "12px",
                fontWeight: 400,
                color: dt.textMuted,
                fontFamily: dt.font,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {userInfo?.email || ""}
            </Box>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ height: "1px", backgroundColor: dt.cardBorder, mx: "6px", mb: 1.5 }} />

        {/* Nav items */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {sections.map((section) => {
            const active = isActive(section.path);
            return (
              <NavLink
                key={section.key}
                to={`/settings/${section.path}`}
                style={{ textDecoration: "none" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "10px 14px",
                    borderRadius: dt.radiusSm,
                    backgroundColor: active ? dt.blueSoft : "transparent",
                    borderLeft: active
                      ? `3px solid ${dt.blue}`
                      : "3px solid transparent",
                    color: active ? dt.blue : dt.textSecondary,
                    fontWeight: active ? 600 : 500,
                    fontSize: "14px",
                    fontFamily: dt.font,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: dt.blueSoft,
                      color: active ? dt.blue : dt.textPrimary,
                    },
                  }}
                >
                  {iconMap[section.iconName]}
                  {translate(section.labelKey)}
                </Box>
              </NavLink>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default SettingsSidebar;
